export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'bot' | 'unknown';

export interface ParsedUA {
	browser: string;
	version?: string;
	os: string;
	device: DeviceType;
}

const BOT_REGEX = /\b(bot|crawler|spider|crawling|googlebot|bingbot|slurp)\b/i;

const detectBrowser = (ua: string): { browser: string; version?: string } => {
	const patterns: Array<[string, RegExp]> = [
		['Edge (Chromium)', /EdgA?\/([\d.]+)/i],
		['Edge (Legacy)', /Edge\/([\d.]+)/i],
		['Opera', /OPR\/([\d.]+)|Opera\/([\d.]+)/i],
		['Vivaldi', /Vivaldi\/([\d.]+)/i],
		['Samsung Internet', /SamsungBrowser\/([\d.]+)/i],
		['Chrome', /Chrome\/([\d.]+)/i],
		['Firefox', /Firefox\/([\d.]+)/i],
		['Safari', /Version\/([\d.]+).*Safari\//i],
		['IE', /MSIE\s([\d.]+);|Trident\/.*rv:([\d.]+)/i],
	];

	for (const [browser, pattern] of patterns) {
		const versionMatch = ua.match(pattern);
		if (versionMatch) {
			const version = versionMatch.find(Boolean);
			return { browser, version };
		}
	}

	// fallback: try to pick the first token that looks like a browser
	const generic = ua.match(/([A-Za-z]+)\/([\d.]+)/);
	if (generic) {
		return { browser: generic[1], version: generic[2] };
	}
	return { browser: 'Unknown', version: undefined };
};

const detectOS = (ua: string): string => {
	const osConfig = [
		{
			os: 'Windows 10+',
			pattern: /Windows NT 10\.0|Windows 10/i,
		},
		{
			os: 'Windows',
			pattern: /Windows NT 6\./i,
		},
		{
			os: 'Android',
			pattern: /Android/i,
		},
		{
			os: 'iOS',
			pattern: /\b(iPhone|iPad|iPod)\b/i,
		},
		{
			os: 'macOS',
			pattern: /Mac OS X|Macintosh/i,
		},
		{
			os: 'Chrome OS',
			pattern: /CrOS/i,
		},
		{
			os: 'Linux',
			pattern: /Linux/i,
		},
	];

	return osConfig.find((item) => item.pattern.test(ua))?.os || 'Unknown';
};

const detectDevice = (ua: string): DeviceType => {
	const deviceTypeConfig = [
		{ type: 'bot', pattern: BOT_REGEX },
		{ type: 'tablet', pattern: /\biPad\b|\bTablet\b|\bNexus 7\b/i },
		{
			type: 'mobile',
			pattern: /\bMobi\b|\bMobile\b|\bAndroid\b.*\bMobile\b|\biPhone\b/i,
		},
	];

	return (
		(deviceTypeConfig.find((item) => item.pattern.test(ua))?.type as DeviceType) ||
		'desktop'
	);
};

export const parseUserAgent = (uaString: string): ParsedUA => {
	const ua = uaString ?? '';
	const { browser, version } = detectBrowser(ua);
	const os = detectOS(ua);
	const device = detectDevice(ua);
	return { browser, version, os, device };
};
